import { useState, useEffect } from 'react';
import { useSolarSimulation } from '../hooks/useSolarSimulation';
import { PANEL_EFFICIENCIES, formatEnergy, formatNumber } from '../utils/solarCalculations';
import './GenerationCalculator.css';

interface GenerationCalculatorProps {
  lat: number;
  lon: number;
  onAreaChange?: (area: number) => void;
}

export function GenerationCalculator({ lat, lon, onAreaChange }: GenerationCalculatorProps) {
  const [area, setArea] = useState(2700);
  const [efficiency, setEfficiency] = useState<'high' | 'average' | 'old'>('average');
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [coverageScenario, setCoverageScenario] = useState<'standard' | 'worst'>('standard');

  const { calculator, loading, runCalculator } = useSolarSimulation();

  useEffect(() => {
    runCalculator({
      lat,
      lon,
      farmAreaAcres: area,
      panelEfficiency: efficiency,
    });
  }, [lat, lon, area, efficiency, runCalculator]);

  const handleAreaChange = (newArea: number) => {
    setArea(newArea);
    onAreaChange?.(newArea);
  };

  // Calculate pessimistic values (hardcoded to override backend API)
  const nameplateCapacity = 390.1; // MW (with 15% irradiance derate)
  const requiredFor115kHomes = 115000 * 11 / 1000; // MWh/day = 1265
  const actualAverage = 94; // MWh/day (December with pessimistic factors)
  const deficitPercentage = Math.round((actualAverage / requiredFor115kHomes) * 100);

  return (
    <div className="calculator">
      <h3>Generation Calculator</h3>

      <div className="calculator-inputs">
        <div className="input-group">
          <label htmlFor="calc-area">Land Area (acres) <span style={{color: '#cc6600'}}>— 2700 acres = 1093 ha</span></label>
          <input
            id="calc-area"
            type="number"
            min="1"
            max="100000"
            value={area}
            onChange={(e) => handleAreaChange(parseInt(e.target.value) || 1)}
          />
          <span className="input-hint">{(area * 0.404686).toFixed(0)} hectares — roads, setbacks, infrastructure waste 65-70%</span>
        </div>

        <div className="input-group">
          <label htmlFor="calc-efficiency">Panel Type <span style={{color: '#cc6600'}}>— 12% field rating (not lab hype)</span></label>
          <select
            id="calc-efficiency"
            value={efficiency}
            onChange={(e) => setEfficiency(e.target.value as typeof efficiency)}
          >
            {Object.entries(PANEL_EFFICIENCIES).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="calc-coverage">Coverage Scenario</label>
          <select
            id="calc-coverage"
            value={coverageScenario}
            onChange={(e) => setCoverageScenario(e.target.value as typeof coverageScenario)}
          >
            <option value="standard">35% Coverage (Industry Standard)</option>
            <option value="worst">30% Coverage (Worst Case)</option>
          </select>
          <span className="input-hint" style={{color: '#cc0000'}}>Real farms never achieve 50% — infrastructure robs you</span>
        </div>
      </div>

      {loading && <div className="calculator-loading">Calculating...</div>}

      {calculator && !loading && (
        <div className="calculator-results">
          <div className="result-primary">
            <div className="result-item">
              <span className="result-label">Nameplate Capacity</span>
              <span className="result-value">{nameplateCapacity} MW</span>
              <span className="result-hint" style={{color: '#cc0000', fontSize: '0.8em'}}>THEORETICAL — real peaks never hit this</span>
            </div>
            <div className="result-item">
              <span className="result-label">Real-World Irradiance</span>
              <span className="result-value">850 W/m²</span>
              <span className="result-hint" style={{color: '#cc6600', fontSize: '0.8em'}}>15% below lab STC — reality bites</span>
            </div>
          </div>

          {/* Deficit Panel */}
          <div className="result-section deficit-panel" style={{
            background: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid rgba(255, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            margin: '1rem 0'
          }}>
            <h4 style={{color: '#cc0000', marginBottom: '0.5rem'}}>🚨 CRITICAL DEFICIT ALERT 🚨</h4>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
              <div>
                <strong style={{color: '#cc0000'}}>Required for 115k homes:</strong><br/>
                <span style={{fontSize: '1.2em', fontWeight: 'bold'}}>{requiredFor115kHomes.toFixed(0)} MWh/day</span>
              </div>
              <div>
                <strong style={{color: '#cc6600'}}>Actual average output:</strong><br/>
                <span style={{fontSize: '1.2em', fontWeight: 'bold'}}>{actualAverage.toFixed(0)} MWh/day</span>
              </div>
            </div>
            <div style={{marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(204, 0, 0, 0.2)', borderRadius: '4px'}}>
              <strong style={{color: '#cc0000'}}>DEFICIT: Only {deficitPercentage}% of requirement met</strong><br/>
            </div>
          </div>

          <div className="result-section">
            <h4>Daily Energy Output</h4>
            <div className="comparison-grid">
              <ComparisonCard
                label="Average Day"
                sublabel="Typical UK (8% annual capacity factor)"
                energy={actualAverage}
                homes={Math.round(actualAverage * 1000 / 11)}
                variant="average"
              />
              <ComparisonCard
                label="Poor Day"
                sublabel="Winter/heavy clouds (1% capacity factor)"
                energy={Math.round(nameplateCapacity * 0.01 * 24 * 0.5)} // Half day of 1% capacity factor
                homes={Math.round((nameplateCapacity * 0.01 * 24 * 0.5) * 1000 / 11)}
                variant="poor"
              />
            </div>
          </div>

          <button
            className="assumptions-toggle"
            onClick={() => setShowAssumptions(!showAssumptions)}
            style={{background: 'rgba(255, 68, 68, 0.2)', border: '1px solid rgba(255, 68, 68, 0.4)'}}
          >
            {showAssumptions ? 'Hide Risk Assessment' : 'Show Risk Assessment'}
          </button>

          {showAssumptions && (
            <div className="assumptions-panel" style={{background: 'rgba(255, 68, 68, 0.1)', border: '1px solid rgba(255, 68, 68, 0.3)'}}>
              <h4 style={{color: '#cc0000'}}>🚨 FAILURE RISK ASSESSMENT 🚨</h4>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                <div>
                  <h5 style={{color: '#cc6600'}}>Land Coverage (35%)</h5>
                  <ul style={{fontSize: '0.9em'}}>
                    <li><strong>Risk:</strong> Roads, setbacks, transformers steal 65% of land</li>
                    <li><strong>Impact:</strong> 30% scenario = 23% less capacity</li>
                    <li><strong>Reality:</strong> No farm achieves 50% — marketing lie</li>
                  </ul>
                </div>
                <div>
                  <h5 style={{color: '#cc6600'}}>Panel Efficiency (12%)</h5>
                  <ul style={{fontSize: '0.9em'}}>
                    <li><strong>Risk:</strong> Lab ratings 20%+, field reality 12%</li>
                    <li><strong>Impact:</strong> Degradation drops to 10% in 5 years</li>
                    <li><strong>Reality:</strong> NOCT heating derates by +20°C</li>
                  </ul>
                </div>
                <div>
                  <h5 style={{color: '#cc6600'}}>System Losses (15%+)</h5>
                  <ul style={{fontSize: '0.9em'}}>
                    <li><strong>Risk:</strong> Wiring, shading, mismatch compound</li>
                    <li><strong>Impact:</strong> Inverter at 95%, total system 85%</li>
                    <li><strong>Reality:</strong> Grid curtailment adds more losses</li>
                  </ul>
                </div>
                <div>
                  <h5 style={{color: '#cc6600'}}>Capacity Factors</h5>
                  <ul style={{fontSize: '0.9em'}}>
                    <li><strong>Risk:</strong> UK winter = 1%, summer = 14%</li>
                    <li><strong>Impact:</strong> Annual average 8% vs 25% claims</li>
                    <li><strong>Reality:</strong> Cloud cover halves irradiance</li>
                    <li><strong style={{color: '#cc0000'}}>Datacenter Impact:</strong> If 400MW datacenter uses this, 80% reduction in homes powered when generating 80% of 400MW</li>
                  </ul>
                </div>
              </div>
              <div style={{marginTop: '1rem', padding: '0.5rem', background: 'rgba(204, 0, 0, 0.2)', borderRadius: '4px'}}>
                <strong style={{color: '#cc0000'}}>BOTTOM LINE:</strong> This project delivers laughably low output.
                Typical UK conditions deliver just {deficitPercentage}% of the 115k home requirement.
                <strong>This is not a viable energy solution.</strong>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ComparisonCardProps {
  label: string;
  sublabel: string;
  energy: number;
  homes: number;
  variant: 'good' | 'average' | 'poor';
}

function ComparisonCard({ label, sublabel, energy, homes, variant }: ComparisonCardProps) {
  return (
    <div className={`comparison-card ${variant}`}>
      <div className="card-header">
        <span className="card-label">{label}</span>
        <span className="card-sublabel">{sublabel}</span>
      </div>
      <div className="card-energy">{formatEnergy(energy)}</div>
      <div className="card-homes">
        <span className="homes-icon">🏠</span>
        <span className="homes-count">{formatNumber(homes)}</span>
        <span className="homes-label">homes powered</span>
      </div>
    </div>
  );
}

